import type { Channel } from "pusher-js";
import Pusher from "pusher-js";
import { isProduction } from "Data/Utils/System";
import type { ActionCreatorWithPayload, AsyncThunk } from "@reduxjs/toolkit";
import { store } from "Data/Redux/Store";
import type { PusherEvent, PusherActionType, PusherEventType } from "Interfaces";
import { selectToken, selectUser, selectPusherKey } from "Data/Redux/Slices/User";
import {
	createHostnamePusherAction, deleteHostnamePusherAction, updateHostnamePusherAction
} from "Data/Redux/Slices/Hostnames";
import { updateAccountPusherAction } from "Data/Redux/Slices/User";
import type { AppState } from "Data/Redux/Reducers";
import type { IndexSignature } from "@clintonelec/typescriptutils";

type EventAction<TPayload, TReturn = void> = AsyncThunk<TPayload, TReturn, { state: AppState }>
	| ActionCreatorWithPayload<TPayload>;

type EventBinding<TPayload, TReturn = void> = {
	action?: EventAction<TPayload, TReturn>;
	callback?: () => void;
	channel?: IndexSignature<EventAction<TPayload, TReturn>>;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const EVENT_BINDINGS: Record<PusherEventType, Record<PusherActionType, EventBinding<any, any>>> = {
	hostname: {
		created: {
			action: createHostnamePusherAction
		},
		updated: {
			action: updateHostnamePusherAction
		},
		deleted: {
			action: deleteHostnamePusherAction
		}
	},
	user: {
		created: {},
		updated: {
			action: updateAccountPusherAction
		},
		deleted: {}
	}
};

export class PusherConnection {
	pusherConnection?: Pusher;
	userChannel?: Channel;

	initializeConnection = () => {
		this.connectPusher();
		this.connectUserChannel();
	};

	connectPusher = () => {
		const state: AppState = store.getState();
		const user = selectUser(state);
		const token = selectToken(state);
		const pusherKey = selectPusherKey(state);
		const authEndpoint = isProduction
			? "https://ddns.clintonelectronics.com/broadcasting/auth"
			: "https://develop.ddns.clintonelectronics.com/broadcasting/auth";

		if (pusherKey && user && !this.pusherConnection) {
			this.pusherConnection = new Pusher(pusherKey, {
				authEndpoint,
				auth: {
					headers: {
						"Authorization": `Bearer ${ token }`
					}
				},
				disableStats: true,
				enabledTransports: [ "ws", "wss" ],
				forceTLS: true,
				wsHost: "conloquium.clintonconnect.com",
				cluster: "mt1"
			});
		}
	};

	connectUserChannel = (reset?: boolean) => {
		const state: AppState = store.getState();
		const user = selectUser(state);
		const channelName = `private-user-${ user?.id }`;

		if (!this.userChannel || this.userChannel.name !== channelName) {
			this.pusherConnection?.allChannels().forEach((channel: Channel) => {
				if (channel.name.indexOf(channelName) >= 0) {
					this.pusherConnection?.unsubscribe(channel.name);
				}
			});

			this.userChannel = this.pusherConnection?.subscribe(channelName);
		}

		if (!this.userChannel?.subscribed || reset) {
			this.userChannel?.unbind_global();
			this.userChannel?.bind_global(this.createPusherHandler(channelName));
		}
	};

	createPusherHandler = (channel: string) => {
		const [ , channelType ] = channel.split("-", 2);

		return (eventType: string, notification: PusherEvent) => {
			if (!notification || !channelType) {
				return;
			}

			const eventBindingType = EVENT_BINDINGS?.[ notification.type ];

			if (eventBindingType && !(notification.event in eventBindingType)) {
				return;
			}

			const eventBinding = eventBindingType[ notification.event ];
			const actionCreator = eventBinding?.channel?.[ channelType ] ?? eventBinding?.action;

			if (actionCreator) {
				const { callback } = eventBinding;

				notification.entities.forEach((id: string) => {
					if (notification.batch) {
						notification.data.forEach(data => {
							Promise.resolve(store.dispatch(actionCreator(
								{ data: { ...data } }
							))).then(callback);
						});
					} else {
						Promise.resolve(store.dispatch(actionCreator(
							{ data: { ...notification.data, id } }
						))).then(callback);
					}
				});
			}
		};
	};

	disconnect = () => {
		this.pusherConnection?.allChannels().forEach((channel: Channel) => {
			this.pusherConnection?.unsubscribe(channel.name);
		});

		this.pusherConnection?.unbind_all();
		this.pusherConnection?.disconnect();
	};
}

export const pusherConnection = new PusherConnection();
